import React, {useState, useEffect} from 'react'
import styled from 'styled-components'
import {inject, observer} from 'mobx-react'
import {Checkbox, Input, Span} from '../../elements/Checkbox'
import {ConstituencyStore} from '../../stores/ConstituencyStore'
import {CandidateStore} from '../../stores/CandidateStore'
import {IConstituency} from '../../types/constituency'
import {CandidateElectionTypes, ElectionData, ICandidate} from '../../types/candidate'
import Autosuggest from '../../elements/Autosuggest/Autosuggest'
import ElectionSelectionConfirmModal, {
  ConstituencyChangeType,
  ElectionChangeType,
  ElectionModalType
} from '../ElectionSelectionConfirmModal/ElectionSelectionConfirmModal'
import './ElectionSelection.scss'

interface SelectedConstituencyInfoProps {
  selectedArea?: [string, ElectionData & {name?: string}]
}

const HELSINKI_OFFICIAL_ID = '091'

const SelectedConstituencyInfo: React.FC<SelectedConstituencyInfoProps> = ({selectedArea}) => {
  if (!selectedArea?.[0] || !selectedArea?.[1] || !Object.keys(selectedArea[1]).length) return null
  const typeInFin = selectedArea[0] === 'county' ? 'alue' : 'kunta'

  return (
    <div className={`selected-constituency-info`}>
      {`Vaalipiiri ${typeInFin}vaaleissa:`} <span>{selectedArea[1].name || ''}</span>
    </div>
  )
}

interface ElectionSelectionProps {
  constituencyStore?: ConstituencyStore
  candidateStore?: CandidateStore
  handleOnChange: (val: any, key: string) => void
  candidate?: ICandidate
}

const ElectionSelection: React.FC<ElectionSelectionProps> = inject(
  'constituencyStore',
  'candidateStore',
  'answerStore'
)(
  observer(({constituencyStore, candidateStore, handleOnChange, candidate}) => {
    const [selectedElectionType, setSelectedElectionType] = useState<string | undefined>(undefined)
    const [selectedMunicipality, setSelectedMunicipality] = useState<IConstituency | undefined>(undefined)
    const [municipalityModalId, setMunicipalityModalId] = useState<number | undefined>(undefined)
    const [municipalityInputValue, setMunicipalityInputValue] = useState('')
    const [modalElection, setModalElection] = useState<string | undefined>()
    const [modalType, setModalType] = useState<ElectionModalType>(undefined)

    const municipalityConstituencies = constituencyStore!.getMunicipalityConstituencies
    const countyConstituencies = constituencyStore!.getCountyConstituencies

    useEffect(() => {
      if (!candidate || !municipalityConstituencies || selectedElectionType !== undefined) return
      const municipality = municipalityConstituencies.find(m => m.id === Number(candidate.hometown))
      setSelectedMunicipality(municipality)

      const election_data = candidate.election_data || {}
      const candidateElections = Object.keys(election_data).filter(key => Object.keys(election_data[key] || {}).length)

      if (candidateElections.includes('municipality') && candidateElections.includes('county') && municipality?.county_id) {
        setSelectedElectionType('both')
      } else if (candidateElections.includes('municipality')) {
        setSelectedElectionType('municipality')
      } else if (candidateElections.includes('county')) {
        setSelectedElectionType('county')
      } else {
        setSelectedElectionType(undefined)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidate, municipalityConstituencies])

    useEffect(() => {
      if (!selectedElectionType || !selectedMunicipality || !candidate) return

      const countyConstituency = countyConstituencies.find(c => c.id === selectedMunicipality.county_id) || {}

      const electionData: Record<string, any> = {}

      if (selectedElectionType === 'municipality' || selectedElectionType === 'both') {
        electionData.municipality = getConstituencyWithPartyId(selectedMunicipality, 'municipality')
      }

      if (selectedElectionType === 'county' || selectedElectionType === 'both') {
        electionData.county = getConstituencyWithPartyId(countyConstituency, 'county')
      }

      handleOnChange(electionData, 'election_data')
    }, [selectedElectionType, selectedMunicipality]) // eslint-disable-line react-hooks/exhaustive-deps

    const getConstituencyWithPartyId = (constituency: Partial<IConstituency>, electionType: 'municipality' | 'county') => {
      const storedCandidate = candidateStore!.candidate
      return {
        ...constituency,
        party_id: storedCandidate?.election_data?.[electionType]?.party_id || storedCandidate?.party_id
      }
    }

    const getChangedConstituencies = (municipalityId: number) => {
      const municipality = municipalityConstituencies.find(m => m.id === municipalityId)
      const currentElections = Object.keys(candidate?.election_data || {}) as CandidateElectionTypes[]

      const changes = currentElections?.reduce((changes, type) => {
        const currentData = candidate?.election_data[type]
        const municipalityId = type === 'county' ? municipality?.county_id : municipality?.id

        const hasConstituencyChange = currentData?.constituency_id !== municipalityId

        if (hasConstituencyChange) {
          changes.push(type)
        }

        return changes
      }, [] as string[])

      return changes.length === 2 ? 'countyMunicipality' : changes[0]
    }

    const handleMunicipalityChange = (id: number) => {
      const municipality = municipalityConstituencies.find(m => m.id === id)
      if (municipality?.official_id === HELSINKI_OFFICIAL_ID) {
        setSelectedElectionType('municipality')
      }
      setSelectedMunicipality(municipality)
      handleOnChange(municipality?.id?.toString() || '', 'hometown')
    }

    const onDropdownSelect = (id: number) => {
      const candidateElections = Object.keys(candidateStore!.candidate?.election_data || {}) || []
      if (candidateElections.length > 0 && candidate?.hometown && candidate.hometown !== id.toString()) {
        const changedConstituencyType = getChangedConstituencies(id) as ConstituencyChangeType

        if (!changedConstituencyType) {
          handleMunicipalityChange(id)
        }

        setModalType(changedConstituencyType)
        setMunicipalityModalId(id)
      } else {
        handleMunicipalityChange(id)
      }
    }

    const handleMunicipalityModalClose = () => {
      if (candidate?.hometown) {
        const municipality = municipalityConstituencies.find(m => m.id === Number(candidate.hometown))
        setMunicipalityInputValue(municipality?.name || '')
      }
      setMunicipalityModalId(undefined)
      setModalType(undefined)
    }

    const handleMunicipalityModalConfirm = () => {
      if (municipalityModalId) {
        handleMunicipalityChange(municipalityModalId)
        setMunicipalityModalId(undefined)
        setModalType(undefined)
      }
    }

    const checkElectionChanges = (candidateElections: string[], selectedValue?: string): ElectionChangeType | undefined => {
      // Early returns for no changes needed
      if (!selectedValue) return undefined
      if (candidateElections.length === 2 && selectedValue === 'both') return undefined

      // Handle election type changes
      if (selectedValue === 'municipality') {
        return candidateElections.includes('county') ? 'electionCounty' : undefined
      }

      if (selectedValue === 'county') {
        return candidateElections.includes('municipality') ? 'electionMunicipality' : undefined
      }

      if (selectedValue === 'both') {
        return 'bothElections'
      }

      return undefined
    }

    const handleRadioChange = (event?: React.ChangeEvent<HTMLInputElement>) => {
      const value = event?.target?.value
      const updateCandidateElections = Object.keys(candidateStore?.candidate?.election_data || {}) || []
      const electionChanges = checkElectionChanges(updateCandidateElections, value)

      const isBothElectionsSelected = value === 'both' && updateCandidateElections.length > 0
      const isChangingFromBothElections = updateCandidateElections.length === 2 && value !== 'both'
      const hasSingleElectionSelected = updateCandidateElections.length === 1

      if (isBothElectionsSelected) {
        setModalElection(value)
        setModalType('bothElections')
      } else if (isChangingFromBothElections) {
        setModalElection(value)
        setModalType(electionChanges)
      } else if (hasSingleElectionSelected) {
        setModalElection(value)
        setModalType('election')
      } else {
        handleOnChange({}, 'election_data')
        setSelectedElectionType(value)
      }
    }

    const handleElectionModalClose = () => {
      setModalElection(undefined)
      setModalType(undefined)
    }

    const handleElectionModalConfirm = () => {
      setSelectedElectionType(modalElection)
      setModalType(undefined)
    }

    const isHelsinki = selectedMunicipality?.official_id === HELSINKI_OFFICIAL_ID

    return (
      <>
        <div className="election-selection">
          <div className="election-selection__title">* Olen ehdolla seuraavissa vaaleissa:</div>
          <div className="election-selection__btn-container">
            <RadioButton value="municipality" checked={selectedElectionType === 'municipality'} onChange={handleRadioChange} />
            Kuntavaalit
          </div>
          <div className={`election-selection__btn-container ${isHelsinki ? 'font-color-gray' : ''}`}>
            <RadioButton value="county" checked={selectedElectionType === 'county'} onChange={handleRadioChange} />
            Aluevaalit
          </div>
          <div className={`election-selection__btn-container ${isHelsinki ? 'font-color-gray' : ''}`}>
            <RadioButton value="both" checked={selectedElectionType === 'both'} onChange={handleRadioChange} />
            Molemmat vaalit
          </div>
          <div className="selection-divider" />
          <div className="election-selection__title">* Kotikuntani</div>
          <Autosuggest
            items={municipalityConstituencies}
            onSelect={id => onDropdownSelect(id)}
            selectedItem={selectedMunicipality?.name}
            placeholder="Valitse kunta"
            inputValue={municipalityInputValue}
            setInputValue={setMunicipalityInputValue}
          />
          {Object.entries(candidate?.election_data || {})?.map(electionData => (
            <SelectedConstituencyInfo key={`selected-area-${electionData[0]}`} selectedArea={electionData} />
          ))}
        </div>
        <ElectionSelectionConfirmModal
          open={!!(modalType && Object.keys(candidateStore!.candidate?.election_data || {}).length)}
          type={modalType}
          onClose={
            modalType === 'municipality' || modalType === 'countyMunicipality' || modalType === 'county'
              ? handleMunicipalityModalClose
              : handleElectionModalClose
          }
          onConfirm={
            modalType === 'municipality' || modalType === 'countyMunicipality' || modalType === 'county'
              ? handleMunicipalityModalConfirm
              : handleElectionModalConfirm
          }
        />
      </>
    )
  })
)

const RadioButton = styled(Checkbox).attrs({type: 'radio'})`
  ${Span} {
    border-radius: 50%;
    border: 1px solid #222;
    box-shadow: unset;
  }

  ${Input} {
    &:checked ~ ${Span}:after {
      display: block;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background-color: #222;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      position: absolute;
    }

    &:checked ~ ${Span} {
      background-color: #fff;
      border-radius: 50%;
    }
  }
`

export default ElectionSelection
